import 'dayjs/locale/fr-ca'

import { useMemo } from 'react'

import { useQueries, useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { countBy, sortBy, uniq } from 'lodash-es'

import {
  operatingRegionsQuery,
  operatingRegionTeamsQuery,
  operatingRegionTransportRequestsQuery,
  regionsQuery,
} from '@/state/regions/regions'
import { type OperatingRegion, type Region, type TeamsStats, type TransportRequestsStats } from '@/state/regions/types'

export interface UseOperatingRegionsPageOptions {
  showTest?: boolean
  selectedDate?: string // YYYY-MM-DD format, undefined = live mode
}

export interface AvailableDate {
  date: string // YYYY-MM-DD
  label: string // Formatted label for display
}

export interface UseOperatingRegionsPageReturn {
  regions: OperatingRegion[]
  isHistoricalMode: boolean
  availableDates: AvailableDate[]
}

const filterActiveRegions = (regions: Region[], bypassTimeCheck = false): Region[] =>
  regions.filter((region) => {
    // When bypassTimeCheck is true (showTest mode), show all regions with a currentOperatingDate
    if (bypassTimeCheck) {
      return region.currentOperatingDate?.eveningId != null
    }

    const date = dayjs(region.currentOperatingDate?.date)
    const startHour = dayjs(region.currentOperatingDate?.startTimestamp)
    // Still show an evening 6 hours after it's over
    const endTime = dayjs(region.currentOperatingDate?.endTimestamp).add(6, 'hour')
    // If during the day of the evening, or between the start and end time of the evening
    const isEveningStillGood =
      date.isSame(dayjs(), 'day') || (startHour && dayjs().isAfter(startHour) && endTime && dayjs().isBefore(endTime))
    return isEveningStillGood
  })

interface RegionWithEvening {
  region: Region
  eveningId: number
}

const filterRegionsWithClosedEveningOnDate = (regions: Region[], targetDate: string): RegionWithEvening[] =>
  regions
    .map((region) => {
      // Find a CLOSED evening on the target date
      // Use toISOString().split('T')[0] to get UTC date without timezone shift
      const evening = region.evenings?.find(
        (e) => e.status === 'CLOSED' && e.date.toISOString().split('T')[0] === targetDate
      )
      if (evening?.eveningId) {
        return { region, eveningId: evening.eveningId }
      }
      return null
    })
    .filter((item): item is RegionWithEvening => item !== null)

export const useOperatingRegionsPage = ({
  showTest,
  selectedDate,
}: UseOperatingRegionsPageOptions): UseOperatingRegionsPageReturn => {
  const isHistoricalMode = !!selectedDate

  // In historical mode, fetch all regions; in live mode, fetch only operating regions
  const { data: allRegionsSnapshot } = useSuspenseQuery(regionsQuery())
  const { data: operatingRegionsSnapshot } = useSuspenseQuery(operatingRegionsQuery())

  // Get regions and their evening IDs based on mode
  const { regionsToShow, eveningIdMap } = useMemo(() => {
    if (isHistoricalMode) {
      // Historical mode: filter regions that had a CLOSED evening on the selected date
      const regionsWithEvening = filterRegionsWithClosedEveningOnDate(allRegionsSnapshot.data, selectedDate)
      const map: Record<string, number> = {}
      regionsWithEvening.forEach(({ region, eveningId }) => {
        map[region._id] = eveningId
      })
      return {
        regionsToShow: regionsWithEvening.map((r) => r.region),
        eveningIdMap: map,
      }
    }

    // Live mode: use current operating regions
    // When showTest is true, bypass time checks to show all regions with a currentOperatingDate
    const activeRegions = filterActiveRegions(operatingRegionsSnapshot.data, showTest)
    const map: Record<string, number> = {}
    activeRegions.forEach((region) => {
      if (region.currentOperatingDate?.eveningId) {
        map[region._id] = region.currentOperatingDate.eveningId
      }
    })
    return {
      regionsToShow: activeRegions,
      eveningIdMap: map,
    }
  }, [isHistoricalMode, selectedDate, allRegionsSnapshot.data, operatingRegionsSnapshot.data, showTest])

  // Get unique evening IDs
  const eveningIds = useMemo(() => Object.values(eveningIdMap), [eveningIdMap])

  // In historical mode, use one-time reads instead of snapshot listeners
  // since closed evening data won't change
  const isLive = !isHistoricalMode

  const teamQueries = useMemo(
    () => eveningIds.map((eveningId) => operatingRegionTeamsQuery(eveningId, isLive)),
    [eveningIds, isLive]
  )
  const teamResults = useQueries({ queries: teamQueries })

  const transportRequestQueries = useMemo(
    () => eveningIds.map((eveningId) => operatingRegionTransportRequestsQuery(eveningId, isLive)),
    [eveningIds, isLive]
  )
  const transportRequestResults = useQueries({ queries: transportRequestQueries })

  // Build teams stats map
  const teamsStatsMap = useMemo(() => {
    const map: Record<number, TeamsStats> = {}
    eveningIds.forEach((eveningId, index) => {
      const result = teamResults[index]
      if (result.data) {
        const teams = result.data.data
        map[eveningId] = {
          ...countBy(teams, (team) => team.status),
          active: teams.filter((team) => team.status !== 'DISSOLVED' && team.status !== 'IN_RETURN').length,
          total: teams.length,
          messageCount: teams.reduce((acc, team) => acc + (team.messagingStatus?.teamMessageCount ?? 0), 0),
          hasUnreadTeamMessages: teams.some(
            (team) => team.status !== 'DISSOLVED' && team.messagingStatus?.hasUnreadTeamMessages
          )
            ? 1
            : 0,
          hasUnreadAdminMessages: teams.some(
            (team) => team.status !== 'DISSOLVED' && team.messagingStatus?.hasUnreadAdminMessages
          )
            ? 1
            : 0,
        }
      }
    })
    return map
  }, [eveningIds, teamResults])

  // Build transport requests stats map
  const transportRequestsStatsMap = useMemo(() => {
    const map: Record<number, TransportRequestsStats> = {}
    eveningIds.forEach((eveningId, index) => {
      const result = transportRequestResults[index]
      if (result.data) {
        const transportRequests = result.data.data
        map[eveningId] = {
          ...countBy(transportRequests, (tr) => tr.status),
          active: transportRequests.filter((tr) => !['completed', 'cancelled', 'unfindable'].includes(tr.status))
            .length,
          total: transportRequests.length,
          mobile: transportRequests.filter((tr) => tr.type === 'mobile').length,
        }
      }
    })
    return map
  }, [eveningIds, transportRequestResults])

  // Combine regions with their stats
  const operatingRegions = useMemo(
    (): OperatingRegion[] =>
      sortBy(
        regionsToShow
          .filter((region) => showTest || !region.test)
          .map((region) => {
            const eveningId = eveningIdMap[region._id]
            return {
              ...region,
              displayEveningId: eveningId,
              transportRequestsCount: transportRequestsStatsMap[eveningId] ?? {
                active: 0,
                total: 0,
                mobile: 0,
              },
              teams: teamsStatsMap[eveningId] ?? {
                active: 0,
                total: 0,
                messageCount: 0,
                hasUnreadTeamMessages: 0,
                hasUnreadAdminMessages: 0,
              },
            }
          }),
        ['name']
      ),
    [regionsToShow, showTest, eveningIdMap, teamsStatsMap, transportRequestsStatsMap]
  )

  // Build list of available dates from all regions' CLOSED evenings
  const availableDates = useMemo((): AvailableDate[] => {
    const today = dayjs().format('YYYY-MM-DD')
    const allDates: string[] = []
    allRegionsSnapshot.data
      .filter((region) => showTest || !region.test) // Filter out test regions if showTest is false
      .forEach((region) => {
        region.evenings?.forEach((evening) => {
          // Include CLOSED and OPEN evenings (OPEN for admins who forgot to close)
          if ((evening.status === 'CLOSED' || evening.status === 'OPEN') && evening.eveningId) {
            // Use toISOString().split('T')[0] to get UTC date without timezone shift
            const dateStr = evening.date.toISOString().split('T')[0]
            // Only include dates up to today (no future dates)
            if (dateStr <= today) {
              allDates.push(dateStr)
            }
          }
        })
      })
    // Get unique dates and sort descending (most recent first)
    const uniqueDates = uniq(allDates).sort((a, b) => b.localeCompare(a))
    return uniqueDates.map((date) => ({
      date,
      label: dayjs(date).locale('fr-ca').format('dddd D MMMM YYYY'),
    }))
  }, [allRegionsSnapshot.data, showTest])

  return {
    regions: operatingRegions,
    isHistoricalMode,
    availableDates,
  }
}
